import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { GreenText, LoadingContent, OrderableTableCell } from 'components'
import { getVehicles } from 'controllers/outboundTrips/index'
import { OutboundTripDetailVehicleDto } from 'dtos/outboundTrips/OutboundTripDetailVehicleDto'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, formatDateMoment } from 'utils'

interface AddVehiclesDialogProps {
  isLoading: boolean
  onClose: () => void
  onSave: (values: OutboundTripDetailVehicleDto[]) => void
  open: boolean
}

export default function AddVehiclesDialog({ isLoading, onClose, onSave, open }: AddVehiclesDialogProps) {
  const [isGetting, setIsGetting] = useState<boolean>(false)
  const [vehicles, setVehicles] = useState<OutboundTripDetailVehicleDto[]>([])
  const [selectedVehicles, setSelectedVehicles] = useState<OutboundTripDetailVehicleDto[]>([])

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OutboundTripDetailVehicleDto>('importerStockNumber')

  useEffect(() => {
    if (open) {
      setIsGetting(true)
      getVehicles()
        .then(res => setVehicles(res.value))
        .finally(() => setIsGetting(false))
    } else {
      setVehicles([])
      setSelectedVehicles([])
    }
  }, [open])

  const onChangeSortByDirection = (propertyName: keyof OutboundTripDetailVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <Dialog open={open} maxWidth='xl' fullWidth>
      <DialogContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item>
            <DialogTitle>Add Vehicles</DialogTitle>
          </Grid>

          <LoadingContent isLoading={isGetting}>
            <Grid item xs={12}>
              <Card variant='outlined' sx={{ width: '100%' }}>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.length === vehicles.length && selectedVehicles.length !== 0}
                            disabled={isLoading || !vehicles.length}
                            indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== vehicles.length}
                            onChange={() => {
                              if (selectedVehicles.length === vehicles.length) {
                                setSelectedVehicles([])
                              } else if (vehicles) {
                                setSelectedVehicles(vehicles)
                              }
                            }}
                          />
                        </TableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='importerStockNumber'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Importer Stock #
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                          Last 6 Of VIN
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='year'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Year
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='make'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Make
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='model'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Model
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='exporterBuyerCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Exporter/Buyer
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='submittedToDOTDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Submitted To DOT
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='readyForOutboundDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Ready For Outbound Date
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='vehicleReleaseDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Vehicle Release Date
                        </OrderableTableCell>

                        <OrderableTableCell<OutboundTripDetailVehicleDto>
                          keyName='bondReleaseDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Bond Release Date
                        </OrderableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles?.sort(clientSideOrderBy<OutboundTripDetailVehicleDto>(orderBy, order)).map((vehicle, index) => (
                        <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)}
                              disabled={isLoading}
                              onChange={() => {
                                if (selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)) {
                                  setSelectedVehicles(selectedVehicles.filter(selectedVehicle => selectedVehicle.vehicleGuid !== vehicle.vehicleGuid))
                                } else {
                                  setSelectedVehicles([...selectedVehicles, vehicle])
                                }
                              }}
                            />
                          </TableCell>

                          <TableCell>{vehicle.importerStockNumber}</TableCell>

                          <TableCell>{vehicle.vin.substring(vehicle.vin.length - 6)}</TableCell>

                          <TableCell>{vehicle.year}</TableCell>

                          <TableCell>{vehicle.make}</TableCell>

                          <TableCell>{vehicle.model}</TableCell>

                          <TableCell>{vehicle.exporterBuyerCompany}</TableCell>

                          <TableCell>{vehicle.submittedToDOTDateTime ? formatDateMoment(vehicle.submittedToDOTDateTime) : '-'}</TableCell>

                          <TableCell>{vehicle.readyForOutboundDateTime ? formatDateMoment(vehicle.readyForOutboundDateTime) : '-'}</TableCell>

                          <TableCell>{vehicle.vehicleReleaseDateTime ? formatDateMoment(vehicle.vehicleReleaseDateTime) : '-'}</TableCell>

                          <TableCell>{vehicle.bondReleaseDateTime ? formatDateMoment(vehicle.bondReleaseDateTime) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </LoadingContent>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Add Vehicles Dialog:**

The Add Vehicles list shows all vehicles eligible for the Outbound Trip. A vehicle is eligible if its Ready for Outbound date or Bond Release Date (found under Vehicle Details / Conformity (tab)) is today or earlier, and it is not already associated with another Outbound Trip. There is no restriction based on the selected Outbound Trip Destination or Outbound Trip Destination Address. 

**Checkbox Select:**

To add a vehicle, select the checkbox next to it.

**CANCEL Button:**

Clicking Cancel will close the dialog without saving changes. 

**SAVE Button:**

Clicking Save adds the selected vehicles to the Outbound Trip.`}
            </GreenText>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
          CANCEL
        </Button>

        <LoadingButton disabled={selectedVehicles.length === 0} loading={isLoading} onClick={() => onSave(selectedVehicles)} variant='contained'>
          SAVE
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
